import { pushDataLayerEvent, type DataLayerWindow } from './core';
import type { PageLoadDataLayerEvent, PageLoadMetadata, ResolvedTheme } from './types';

export function resolveTheme(
  themePreference: string | undefined,
  prefersDarkMode: boolean
): ResolvedTheme {
  if (themePreference === 'dark' || themePreference === 'light') {
    return themePreference;
  }

  return prefersDarkMode ? 'dark' : 'light';
}

export function buildPageLoadEvent(
  themePreference: string | undefined,
  prefersDarkMode: boolean,
  now: Date = new Date(),
  metadata: PageLoadMetadata = {}
): PageLoadDataLayerEvent {
  return {
    event: 'page_load',
    mood: resolveTheme(themePreference, prefersDarkMode),
    timestamp: now.toISOString(),
    page_type: metadata.pageType?.trim() ?? '',
    page_url: metadata.pageUrl ?? '',
    page_title: metadata.pageTitle ?? '',
    page_referrer: metadata.pageReferrer ?? ''
  };
}

function resolvePageType(targetDocument: Document): string {
  const metaPageType =
    typeof targetDocument.querySelector === 'function'
      ? targetDocument.querySelector('meta[name="page_type"]')?.getAttribute('content')?.trim()
      : undefined;

  if (metaPageType) return metaPageType;

  return targetDocument.documentElement.dataset.pageType?.trim() ?? '';
}

export function trackPageLoad(
  targetWindow: DataLayerWindow = window,
  targetDocument: Document = document
): void {
  const event = buildPageLoadEvent(
    targetDocument.documentElement.dataset.theme,
    targetWindow.matchMedia('(prefers-color-scheme: dark)').matches,
    new Date(),
    {
      pageType: resolvePageType(targetDocument),
      pageUrl: targetWindow.location.href,
      pageTitle:
        targetDocument
          .querySelector?.('meta[name="analytics_page_title"]')
          ?.getAttribute('content') ?? '',
      pageReferrer: targetDocument.referrer
    }
  );

  pushDataLayerEvent(event, targetWindow);
}
